import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { alpha, useTheme } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import PersonIcon from "@mui/icons-material/Person";
import GroupsIcon from "@mui/icons-material/Groups";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { jwtDecode } from "jwt-decode";
import { API } from "../config/apiConfig";
import { portal, portalGradientTextSx } from "../styles/dashboardBrand";
import DashboardPageHeader from "../components/DashboardPageHeader";
import { useUtcMidnightCountdown, formatUtcMidnightCountdown } from "../hooks/useUtcMidnightCountdown";
import { dreameyDiamondsUserSum } from "../utils/dreameyDiamonds";
import {
  readHomeCache,
  writeHomeCache,
  shouldForceUserDashboardHomeNetwork,
  clearUserDashboardHomeForceNetwork,
} from "../utils/userDashboardHomeCache";
import { buildReferralsActivitySnapshot } from "../utils/referralsActivitySnapshot";

function designationNameFromRecord(record) {
  if (!record || typeof record !== "object") return "";
  const raw =
    record.DesignationName ??
    record.designationName ??
    record.DESIGNATIONNAME;
  const s = String(raw ?? "").trim();
  if (!s || s.toLowerCase() === "null") return "";
  return s;
}

function normalizeStreakPayload(raw) {
  if (!raw || typeof raw !== "object") return null;
  const r = raw.data && typeof raw.data === "object" ? raw.data : raw;
  return {
    adsWatchedToday: Number(r.adsWatchedToday ?? r.adsToday ?? r.AdsWatchedToday ?? r.watchedToday ?? 0) || 0,
    goalPerDay: Number(r.goalPerDay ?? r.goal ?? r.GoalPerDay ?? r.dailyGoal ?? 10) || 10,
    currentStreak: Number(r.currentStreak ?? r.CurrentStreak ?? r.consecutiveDays ?? 0) || 0,
    longestStreak: Number(r.longestStreak ?? r.LongestStreak ?? r.bestStreak ?? 0) || 0,
  };
}

/** Per-referral streak (same fields as Activity page) for dashboard cache / snapshot. */
function normalizeStreakPayloadForRef(raw) {
  if (!raw || typeof raw !== "object") return null;
  const r = raw.data && typeof raw.data === "object" ? raw.data : raw;
  const last30 =
    r.last30Days ?? r.Last30Days ?? r.last_30_days ?? [];
  return {
    adsWatchedToday: Number(
      r.adsWatchedToday ?? r.adsToday ?? r.AdsWatchedToday ?? r.watchedToday ?? 0
    ),
    goalPerDay: Number(r.goalPerDay ?? r.goal ?? r.GoalPerDay ?? r.dailyGoal ?? 5) || 5,
    currentStreak: Number(r.currentStreak ?? r.CurrentStreak ?? r.consecutiveDays ?? 0) || 0,
    longestStreak: Number(r.longestStreak ?? r.LongestStreak ?? r.bestStreak ?? 0) || 0,
    last30Days: Array.isArray(last30) ? last30 : [],
  };
}

/** True when today's ad goal is met (streak uses UTC day from API). */
function isStreakGoalMetToday(adsWatchedToday, goalPerDay) {
  const g = Math.max(1, Number(goalPerDay) || 10);
  return Number(adsWatchedToday ?? 0) >= g;
}

function displayNameFromRecord(obj, fallback = "Member") {
  const first = String(obj?.FirstName ?? obj?.firstName ?? "").trim();
  const last = String(obj?.LastName ?? obj?.lastName ?? "").trim();
  const full = [first, last].filter(Boolean).join(" ");
  if (full) return full;
  const userName = String(obj?.UserName ?? obj?.userName ?? obj?.username ?? "").trim();
  if (userName) return userName;
  return fallback;
}

function truncateToMaxChars(value, maxChars = 16) {
  const s = String(value ?? "");
  if (s.length <= maxChars) return s;
  return s.slice(0, maxChars);
}

/**
 * Rank label from main.Designation (API). Tree "Level" is placement depth only — never use it as a rank title.
 * If Users.DesignationID is NULL, there is no designation row; show Member (or Leader for self when defaultLeader).
 */
function resolveRankLabel(record, designationByUserId, userId, defaultLeader = false) {
  const fromDb = designationNameFromRecord(record);
  if (fromDb) return fromDb;
  const uid = Number(userId);
  if (Number.isFinite(uid) && designationByUserId && typeof designationByUserId === "object") {
    const fromMap = String(designationByUserId[uid] ?? "").trim();
    if (fromMap && fromMap.toLowerCase() !== "null") return fromMap;
  }
  const fromRankFields = String(
    record?.ReferralRank ??
      record?.referralRank ??
      record?.RankName ??
      record?.rankName ??
      record?.RankTitle ??
      record?.rankTitle ??
      ""
  ).trim();
  if (fromRankFields) return fromRankFields;
  return defaultLeader ? "Leader" : "Member";
}

function getEngagementPointsFromEarnings(earnings) {
  if (!earnings || typeof earnings !== "object") return 0;
  const raw =
    earnings.CurrentMonthTokens ??
    earnings.currentMonthTokens ??
    earnings.TotalEngagementPoints ??
    earnings.EngagementPoints ??
    earnings.TotalTokens ??
    earnings.Tokens ??
    earnings.TotalEarnings ??
    0;
  const num = Number(raw);
  return Number.isFinite(num) ? num : 0;
}

function getUserIdFromToken() {
  const token = localStorage.getItem("token") || localStorage.getItem("authToken");
  if (!token) return null;
  try {
    const decoded = jwtDecode(token);
    const userId = Number(decoded?.userId ?? decoded?.UserID ?? decoded?.id);
    return Number.isFinite(userId) ? userId : null;
  } catch {
    return null;
  }
}

/** Same tier thresholds as Activity (Leader → Diamond) — network depth & direct refs. */
const RANK_BAR_DEPTH_THRESHOLDS = [3, 6, 9, 12, 15];
const RANK_BAR_REFERRAL_THRESHOLDS = [2, 4, 6, 8, 10];

function rankMetricToBarFillPercent(rawValue, thresholdsAscending) {
  const d = Math.max(0, Number(rawValue) || 0);
  const tiers = thresholdsAscending;
  const n = tiers.length;
  if (n === 0) return 0;
  const segW = 100 / n;
  if (d >= tiers[n - 1]) return 100;
  if (d < tiers[0]) return tiers[0] > 0 ? (d / tiers[0]) * segW : 0;
  for (let k = 0; k < n - 1; k++) {
    if (d >= tiers[k] && d < tiers[k + 1]) {
      const span = tiers[k + 1] - tiers[k];
      const t = span > 0 ? (d - tiers[k]) / span : 0;
      return (k + 1) * segW + t * segW;
    }
  }
  return 100;
}

function rankLevelsToBarFillPercent(level) {
  return rankMetricToBarFillPercent(level, RANK_BAR_DEPTH_THRESHOLDS);
}

function rankReferralsToBarFillPercent(directRefCount) {
  return rankMetricToBarFillPercent(directRefCount, RANK_BAR_REFERRAL_THRESHOLDS);
}

function maxDepthFromNestedReferralNodes(nodes, depth = 1) {
  if (!Array.isArray(nodes) || nodes.length === 0) return 0;
  let best = depth;
  for (const n of nodes) {
    const children = n.children;
    if (Array.isArray(children) && children.length > 0) {
      best = Math.max(best, maxDepthFromNestedReferralNodes(children, depth + 1));
    }
  }
  return best;
}

function maxPlacementDepthFromDirectReferralsResponse(data) {
  if (!data || typeof data !== "object") return 0;
  const flat = data.flat;
  if (Array.isArray(flat) && flat.length > 0) {
    let m = 0;
    for (const r of flat) {
      const lv = Number(r.Level ?? r.level ?? 0) || 0;
      if (lv > m) m = lv;
    }
    return m;
  }
  const refs = data.referrals;
  if (!Array.isArray(refs) || refs.length === 0) return 0;
  return maxDepthFromNestedReferralNodes(refs, 1);
}

/** Compact label: `hostname/refCode` from `?ref=` — full URL still used for copy/open. */
function referralLinkShortDisplay(fullUrl) {
  if (!fullUrl || typeof fullUrl !== "string") return "";
  try {
    const u = new URL(fullUrl);
    const host = u.hostname.replace(/^www\./i, "");
    const ref = u.searchParams.get("ref") ?? u.searchParams.get("REF");
    if (host && ref) {
      return `${host}/${ref}`;
    }
    const path = u.pathname.replace(/^\/+/, "");
    const fallback = `${path}${u.search || ""}`;
    return fallback || fullUrl;
  } catch {
    return fullUrl;
  }
}

function extractNumericLevelFromRecord(obj) {
  if (!obj || typeof obj !== "object") return null;
  const keys = [
    "Level",
    "level",
    "UserLevel",
    "userLevel",
    "CurrentLevel",
    "currentLevel",
    "MarketingLevel",
    "marketingLevel",
    "ReferralProgramLevel",
    "referralProgramLevel",
  ];
  for (const k of keys) {
    if (obj[k] == null || obj[k] === "") continue;
    const n = Number(obj[k]);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return null;
}

function resolveUserProgramLevel(profileUser, jwtPayload, levelByUserId, selfUserId) {
  const fromMap =
    selfUserId != null && levelByUserId && typeof levelByUserId === "object"
      ? levelByUserId[Number(selfUserId)]
      : null;
  const fromMapNum = fromMap != null ? Number(fromMap) : NaN;
  const merged = {
    ...(profileUser && typeof profileUser === "object" ? profileUser : {}),
    ...(jwtPayload && typeof jwtPayload === "object" ? jwtPayload : {}),
  };
  const fromProfile = extractNumericLevelFromRecord(merged);
  const candidates = [];
  if (Number.isFinite(fromMapNum)) candidates.push(fromMapNum);
  if (fromProfile != null && Number.isFinite(fromProfile)) candidates.push(fromProfile);
  if (candidates.length === 0) return 0;
  return Math.max(...candidates);
}

function ThinLadderBar({ theme, emoji, fillPercent, label }) {
  const fp = Math.min(100, Math.max(0, fillPercent));
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ width: "100%" }}>
      <Typography component="span" sx={{ fontSize: "1rem", lineHeight: 1, flexShrink: 0 }} aria-hidden>
        {emoji}
      </Typography>
      <Box
        component="span"
        sx={{ position: "relative", flex: 1, minWidth: 0 }}
        aria-label={label}
      >
        <Box
          sx={{
            height: 5,
            borderRadius: 999,
            bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.1 : 0.08),
            border: "1px solid",
            borderColor: alpha(theme.palette.divider, 0.85),
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: `${fp}%`,
              height: "100%",
              borderRadius: "inherit",
              background: portal.gradientBar,
              transition: "width 0.45s ease-out",
            }}
          />
        </Box>
      </Box>
    </Stack>
  );
}

function InfoRow({ label, value }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        py: 0.5,
        borderBottom: "1px solid",
        borderColor: alpha("#fff", 0.06),
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 800, color: "common.white" }}>
        {value}
      </Typography>
    </Stack>
  );
}

function getInitialHomeStateFromCache() {
  const uid = getUserIdFromToken();
  if (!uid) {
    return { loading: false, selfInfo: null, referrals: [], error: "" };
  }
  if (shouldForceUserDashboardHomeNetwork()) {
    return { loading: true, selfInfo: null, referrals: [], error: "" };
  }
  const c = readHomeCache(uid);
  if (c?.selfInfo) {
    return { loading: false, selfInfo: c.selfInfo, referrals: c.referrals, error: "" };
  }
  return { loading: true, selfInfo: null, referrals: [], error: "" };
}

export default function Home() {
  const theme = useTheme();
  /** Narrower viewports → shorter names so Member / EP / streak stay on one row on mobile */
  const mq360 = useMediaQuery("(max-width:360px)");
  const mq400 = useMediaQuery("(max-width:400px)");
  const mq480 = useMediaQuery("(max-width:480px)");
  const mq560 = useMediaQuery("(max-width:560px)");
  const mqDownSm = useMediaQuery(theme.breakpoints.down("sm"));
  const referralNameMaxChars = useMemo(() => {
    if (mq360) return 10;
    if (mq400) return 12;
    if (mq480) return 13;
    if (mq560) return 14;
    if (mqDownSm) return 15;
    return 16;
  }, [mq360, mq400, mq480, mq560, mqDownSm]);

  const utcMidnightRemainingMs = useUtcMidnightCountdown();
  const _init = getInitialHomeStateFromCache();
  const [loading, setLoading] = useState(_init.loading);
  const [error, setError] = useState(_init.error);
  const [selfInfo, setSelfInfo] = useState(_init.selfInfo);
  const [referrals, setReferrals] = useState(_init.referrals);
  const [copySnack, setCopySnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [utcNow, setUtcNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setUtcNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const utcTimeStr = useMemo(() => {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "UTC",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    }).format(utcNow);
  }, [utcNow]);

  const copyToClipboard = useCallback(async (text, successMessage) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopySnack({
        open: true,
        message: successMessage || "Copied",
        severity: "success",
      });
    } catch {
      setCopySnack({
        open: true,
        message: "Could not copy",
        severity: "error",
      });
    }
  }, []);

  const referralLayoutChars = useMemo(() => {
    if (!Array.isArray(referrals) || referrals.length === 0) {
      return {
        nameChars: Math.min(8, referralNameMaxChars),
        rankChars: 6,
        epChars: 1,
      };
    }
    const cap = referralNameMaxChars;
    const maxName = referrals.reduce(
      (m, r) => Math.max(m, Math.min(cap, String(r?.name ?? "").length)),
      0
    );
    const maxRank = referrals.reduce(
      (m, r) => Math.max(m, Math.min(16, String(r?.rank ?? "").length)),
      0
    );
    const maxEp = referrals.reduce(
      (m, r) => Math.max(m, String(Number(r?.engagementPoints ?? 0).toLocaleString()).length),
      0
    );
    return {
      nameChars: Math.min(cap, Math.max(1, maxName)),
      rankChars: Math.max(1, maxRank),
      epChars: Math.max(1, maxEp),
    };
  }, [referrals, referralNameMaxChars]);

  const anyDownlineAdGoalOpen = useMemo(
    () =>
      referrals.some((r) => !isStreakGoalMetToday(r.adsWatchedToday, r.goalPerDay)),
    [referrals]
  );

  const token = localStorage.getItem("token") || localStorage.getItem("authToken") || "";
  const userId = useMemo(() => getUserIdFromToken(), [token]);

  const fetchHomeData = useCallback(async () => {
    if (!userId) {
      setError("Please sign in to view Home.");
      setLoading(false);
      return;
    }

    const forceNetwork = shouldForceUserDashboardHomeNetwork();
    const cached = readHomeCache(userId);
    if (!forceNetwork && cached?.selfInfo) {
      setSelfInfo(cached.selfInfo);
      setReferrals(cached.referrals);
      setError("");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const [
        profileRes,
        earningsRes,
        streakRes,
        refsRes,
        levelsRes,
        coinsRes,
        streakCompletersRes,
      ] = await Promise.all([
        fetch(API.userProfile(userId), { headers }),
        fetch(`${API.earningsSummary}/${userId}`, { headers }),
        fetch(`${API.adsStreak}?userId=${encodeURIComponent(userId)}`, { headers }),
        fetch(API.originalReferrals(userId), { headers }),
        fetch(API.getUsersByLevel, { headers }),
        fetch(API.appCoinsApps, { headers }),
        fetch(API.directReferralsStreakCompleters, { headers }),
      ]);

      const profileData = await profileRes.json().catch(() => ({}));
      const earningsData = await earningsRes.json().catch(() => ({}));
      const streakData = await streakRes.json().catch(() => ({}));
      const refsData = await refsRes.json().catch(() => ({}));
      const levelsData = await levelsRes.json().catch(() => ({}));
      const coinsData = await coinsRes.json().catch(() => ({}));
      const streakCompletersData = await streakCompletersRes.json().catch(() => ({}));

      if (!profileRes.ok) {
        throw new Error(profileData?.message || "Failed to load profile");
      }

      const me = profileData?.user ?? profileData ?? {};
      const earnings = earningsData?.earnings ?? earningsData?.data ?? earningsData ?? {};
      const streakNorm = normalizeStreakPayload(streakData);
      const myStreak = streakNorm?.currentStreak ?? 0;
      const directRefs = Array.isArray(refsData?.referrals) ? refsData.referrals : [];

      const levelByUserId = {};
      const designationByUserId = {};
      if (levelsRes.ok && levelsData?.success && Array.isArray(levelsData?.users)) {
        for (const u of levelsData.users) {
          const uid = Number(u?.UserID ?? u?.userId);
          if (!Number.isFinite(uid)) continue;
          levelByUserId[uid] = Number(u?.Level ?? u?.level ?? 0) || 0;
          const dn = designationNameFromRecord(u);
          if (dn) designationByUserId[uid] = dn;
        }
      }

      const selfRank = resolveRankLabel(
        me,
        designationByUserId,
        userId,
        directRefs.length > 0
      );

      let placementMaxDepth = null;
      const refCode = String(me?.ReferralCode ?? me?.referralCode ?? "").trim();
      if (refCode) {
        try {
          const treeRes = await fetch(API.directReferrals(encodeURIComponent(refCode)), { headers });
          const treeData = await treeRes.json().catch(() => ({}));
          if (treeRes.ok) {
            placementMaxDepth = maxPlacementDepthFromDirectReferralsResponse(treeData);
          }
        } catch {
          /* network tree optional for home bars */
        }
      }

      let jwtPayload = null;
      try {
        jwtPayload = token ? jwtDecode(token) : null;
      } catch {
        jwtPayload = null;
      }
      const legacyProgramLevel = resolveUserProgramLevel(me, jwtPayload, levelByUserId, userId);
      const rankQualifyingLevel =
        placementMaxDepth != null ? Math.max(legacyProgramLevel, placementMaxDepth) : legacyProgramLevel;
      const directRefCount = directRefs.length;
      const levelsBarPercent = rankLevelsToBarFillPercent(rankQualifyingLevel);
      const referralsBarPercent = rankReferralsToBarFillPercent(directRefCount);
      const todayGoalPercent =
        (streakNorm?.goalPerDay ?? 0) > 0
          ? Math.min(100, ((streakNorm?.adsWatchedToday ?? 0) / (streakNorm?.goalPerDay ?? 1)) * 100)
          : 0;

      const myReferralCode = (
        refCode ||
        String(jwtPayload?.referralCode ?? jwtPayload?.ReferralCode ?? "").trim()
      );
      const myReferralLink = myReferralCode
        ? `https://dv-apps.com/signup?ref=${encodeURIComponent(myReferralCode)}`
        : "";

      let totalDreameyDiamonds = 0;
      if (coinsRes.ok && coinsData?.success) {
        totalDreameyDiamonds = dreameyDiamondsUserSum(coinsData);
      }

      const refStreakByUserId = new Map();

      const referralRows = await Promise.all(
        directRefs.map(async (ref) => {
          const rid = Number(ref?.UserID ?? ref?.userId);
          if (!Number.isFinite(rid)) return null;

          const [refEarningsRes, refStreakRes] = await Promise.all([
            fetch(`${API.earningsSummary}/${rid}`, { headers }).catch(() => null),
            fetch(`${API.adsStreak}?userId=${encodeURIComponent(rid)}`, { headers }).catch(() => null),
          ]);

          let refEngagement = 0;
          if (refEarningsRes && refEarningsRes.ok) {
            const refEarningsData = await refEarningsRes.json().catch(() => ({}));
            const refEarnings = refEarningsData?.earnings ?? refEarningsData?.data ?? refEarningsData ?? {};
            refEngagement = getEngagementPointsFromEarnings(refEarnings);
          }

          let refCurrentStreak = 0;
          let refAdsWatchedToday = 0;
          let refGoalPerDay = 10;
          if (refStreakRes && refStreakRes.ok) {
            const refStreakData = await refStreakRes.json().catch(() => ({}));
            const refFull = normalizeStreakPayloadForRef(refStreakData);
            if (refFull) refStreakByUserId.set(rid, refFull);
            const refStreakNorm = normalizeStreakPayload(refStreakData);
            refCurrentStreak = refStreakNorm?.currentStreak ?? 0;
            refAdsWatchedToday = refStreakNorm?.adsWatchedToday ?? 0;
            refGoalPerDay = refStreakNorm?.goalPerDay ?? 10;
          }
          const refTodayGoalPercent =
            refGoalPerDay > 0 ? Math.min(100, (refAdsWatchedToday / refGoalPerDay) * 100) : 0;

          return {
            userId: rid,
            name: displayNameFromRecord(ref),
            rank: resolveRankLabel(ref, designationByUserId, rid, false),
            engagementPoints: refEngagement,
            currentStreak: refCurrentStreak,
            adsWatchedToday: refAdsWatchedToday,
            goalPerDay: refGoalPerDay,
            todayGoalPercent: refTodayGoalPercent,
          };
        })
      );

      const rows = referralRows.filter(Boolean);
      const goalMet = (r) => {
        const g = Math.max(1, Number(r.goalPerDay) || 10);
        return Number(r.adsWatchedToday ?? 0) >= g;
      };
      const incomplete = rows.filter((r) => !goalMet(r));
      incomplete.sort((a, b) => {
        const ga = Math.max(1, Number(a.goalPerDay) || 10);
        const gb = Math.max(1, Number(b.goalPerDay) || 10);
        const ra = Number(a.adsWatchedToday ?? 0) / ga;
        const rb = Number(b.adsWatchedToday ?? 0) / gb;
        if (rb !== ra) return rb - ra;
        return (b.todayGoalPercent ?? 0) - (a.todayGoalPercent ?? 0);
      });

      let referralsActivity = null;
      if (refsRes.ok && levelsRes.ok) {
        try {
          referralsActivity = buildReferralsActivitySnapshot({
            listData: refsData,
            streakCompletersData: streakCompletersRes.ok ? streakCompletersData : {},
            levelData: levelsData,
            refStreakByUserId,
          });
        } catch {
          referralsActivity = null;
        }
      }

      const nextSelfInfo = {
        name: displayNameFromRecord(me, "You"),
        rank: selfRank,
        engagementPoints: getEngagementPointsFromEarnings(earnings),
        currentStreak: myStreak,
        levelsBarPercent,
        referralsBarPercent,
        todayGoalPercent,
        adsWatchedToday: streakNorm?.adsWatchedToday ?? 0,
        goalPerDay: streakNorm?.goalPerDay ?? 10,
        referralLink: myReferralLink,
        totalDreameyDiamonds,
      };
      setSelfInfo(nextSelfInfo);
      setReferrals(incomplete);
      writeHomeCache(userId, nextSelfInfo, incomplete, {
        profile: profileData,
        earnings: earningsData,
        streak: streakData,
        refs: refsData,
        levels: levelsData,
        coins: coinsData,
        streakCompleters: streakCompletersData,
        referralsActivity,
        placementMaxDepth,
      });
      clearUserDashboardHomeForceNetwork();
    } catch (e) {
      setError(e?.message || "Failed to load home data");
      setSelfInfo(null);
      setReferrals([]);
    } finally {
      setLoading(false);
    }
  }, [token, userId]);

  useEffect(() => {
    void fetchHomeData();
  }, [fetchHomeData]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 980, mx: "auto", py: { xs: 1, sm: 2 }, px: { xs: 0.5, sm: 1 } }}>
      <DashboardPageHeader
        icon={HomeIcon}
        title="Home"
        titleTrail={
          selfInfo ? (
            <Stack
              direction="row"
              alignItems="flex-start"
              spacing={1.5}
              flexWrap="wrap"
              useFlexGap
              sx={{ transform: { md: "translateY(-0.2rem)" } }}
            >
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: alpha(theme.palette.divider, 0.85),
                  borderRadius: 2,
                  px: 1.25,
                  py: 0.6,
                  background:
                    theme.palette.mode === "dark"
                      ? `linear-gradient(145deg, ${alpha("#1a1f2e", 0.9)} 0%, ${alpha("#10131d", 0.9)} 100%)`
                      : alpha(theme.palette.background.paper, 0.95),
                }}
              >
                <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
                  Time (UTC)
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 800,
                    color: "common.white",
                    mt: 0.25,
                    fontVariantNumeric: "tabular-nums",
                    lineHeight: 1.25,
                  }}
                >
                  {utcTimeStr}
                </Typography>
              </Box>
              <Box
                sx={{
                  minWidth: 0,
                  pt: 0,
                  mt: 0,
                  textAlign: "left",
                }}
              >
                <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
                  Your referral link
                </Typography>
                {selfInfo.referralLink ? (
                  <Link
                    href={selfInfo.referralLink}
                    onClick={(e) => {
                      e.preventDefault();
                      void copyToClipboard(selfInfo.referralLink, "Referral link copied");
                    }}
                    variant="body2"
                    title={`Click to copy: ${selfInfo.referralLink}`}
                    aria-label={`Copy full referral link ${selfInfo.referralLink}`}
                    sx={{
                      wordBreak: "break-all",
                      cursor: "pointer",
                      display: "inline-block",
                      mt: 0.25,
                    }}
                  >
                    {referralLinkShortDisplay(selfInfo.referralLink)}
                  </Link>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }} component="span" display="block">
                    —
                  </Typography>
                )}
              </Box>
            </Stack>
          ) : null
        }
      />

      <Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
        <Paper
          component={RouterLink}
          to="/dashboard/apps"
          elevation={0}
          sx={(theme) => ({
            display: "inline-block",
            width: "fit-content",
            textDecoration: "none",
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2,
            background:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.06)"
                : "rgba(255,255,255,0.8)",
            backdropFilter: "blur(10px)",
            border:
              theme.palette.mode === "dark"
                ? "1px solid rgba(255,255,255,0.08)"
                : "1px solid rgba(255,255,255,0.2)",
            transition: "background 0.2s, border-color 0.2s",
            "&:hover": {
              background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.95)",
              borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.4)",
            },
            "@keyframes popInOut": {
              "0%, 200%": { transform: "scale(1)", opacity: 1.1 },
              "50%": { transform: "scale(1.08)", opacity: 0.8 },
            },
            animation: "popInOut 1.5s ease-in-out infinite",
          })}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "#22c55e",
              fontSize: { xs: "0.875rem", sm: "1.125rem" },
              textAlign: "center",
            }}
          >
            Click here to download our Dreamey Vids App
          </Typography>
        </Paper>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {selfInfo ? (
        <Card
          sx={{
            mb: 2,
            borderRadius: 3,
            border: "1px solid",
            borderColor: alpha(theme.palette.divider, 0.9),
            background: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.55 : 0.92),
            backdropFilter: "blur(12px)",
            boxShadow:
              theme.palette.mode === "dark"
                ? `0 8px 40px ${alpha("#000", 0.3)}, inset 0 1px 0 ${alpha("#fff", 0.04)}`
                : `0 8px 30px ${alpha(theme.palette.primary.main, 0.08)}`,
          }}
        >
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <PersonIcon sx={{ color: portal.amber }} />
                <Typography
                  component={RouterLink}
                  to="/dashboard/ad-streak"
                  variant="h6"
                  title="Go to My Activity"
                  sx={{
                    ...portalGradientTextSx({ fontWeight: 800 }),
                    fontSize: "1.1rem",
                    whiteSpace: "nowrap",
                    display: "inline-block",
                    textDecoration: "underline",
                    textUnderlineOffset: "0.16em",
                    textDecorationThickness: "2px",
                    cursor: "pointer",
                    "&:hover": { opacity: 0.9 },
                  }}
                >
                  YOU
                </Typography>
              </Box>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: alpha(theme.palette.divider, 0.85),
                  borderRadius: 2,
                  px: 1.25,
                  py: 0.6,
                  background:
                    theme.palette.mode === "dark"
                      ? `linear-gradient(145deg, ${alpha("#1a1f2e", 0.9)} 0%, ${alpha("#10131d", 0.9)} 100%)`
                      : alpha(theme.palette.background.paper, 0.95),
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 800,
                    color: theme.palette.success.main,
                    whiteSpace: "nowrap",
                    textAlign: "center",
                  }}
                >
                  {selfInfo.rank}
                </Typography>
              </Box>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: alpha(theme.palette.divider, 0.85),
                  borderRadius: 2,
                  px: 1.25,
                  py: 0.6,
                  background:
                    theme.palette.mode === "dark"
                      ? `linear-gradient(145deg, ${alpha("#1a1f2e", 0.9)} 0%, ${alpha("#10131d", 0.9)} 100%)`
                      : alpha(theme.palette.background.paper, 0.95),
                }}
              >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    EP :
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: "common.white" }}>
                    {selfInfo.engagementPoints.toLocaleString()}
                  </Typography>
                </Stack>
              </Box>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: isStreakGoalMetToday(selfInfo.adsWatchedToday, selfInfo.goalPerDay)
                    ? alpha(theme.palette.divider, 0.85)
                    : alpha(theme.palette.warning.main, 0.55),
                  borderRadius: 2,
                  px: 1.25,
                  py: 0.6,
                  background:
                    theme.palette.mode === "dark"
                      ? `linear-gradient(145deg, ${alpha("#1a1f2e", 0.9)} 0%, ${alpha("#10131d", 0.9)} 100%)`
                      : alpha(theme.palette.background.paper, 0.95),
                }}
              >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: "common.white" }}>
                    {selfInfo.currentStreak} 🔥
                  </Typography>
                  {!isStreakGoalMetToday(selfInfo.adsWatchedToday, selfInfo.goalPerDay) && (
                    <>
                      <Tooltip title="Today's ad goal not completed yet — finish before this UTC timer hits 00:00:00 to keep your streak safe.">
                        <WarningAmberIcon
                          sx={{ fontSize: 22, color: "warning.main" }}
                          aria-label="Today's streak goal not met"
                        />
                      </Tooltip>
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          fontFamily: "ui-monospace, monospace",
                          fontSize: "0.7rem",
                          color: "warning.light",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatUtcMidnightCountdown(utcMidnightRemainingMs)} UTC
                      </Typography>
                    </>
                  )}
                </Stack>
              </Box>
            </Stack>

            <Stack spacing={1} sx={{ mt: 1.5, width: "100%" }}>
              <ThinLadderBar
                theme={theme}
                emoji="🎯"
                fillPercent={selfInfo.todayGoalPercent ?? 0}
                label={`Today's goal progress (${selfInfo.adsWatchedToday ?? 0}/${selfInfo.goalPerDay ?? 10})`}
              />
              <ThinLadderBar
                theme={theme}
                emoji="👤"
                fillPercent={selfInfo.referralsBarPercent ?? 0}
                label={`Direct referrals progress toward rank tiers (counts ${RANK_BAR_REFERRAL_THRESHOLDS.join(
                  ", "
                )})`}
              />
              <ThinLadderBar
                theme={theme}
                emoji="📶"
                fillPercent={selfInfo.levelsBarPercent ?? 0}
                label={`Network depth progress toward rank tiers (depth ${RANK_BAR_DEPTH_THRESHOLDS.join(", ")})`}
              />
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      <Card
        sx={{
          borderRadius: 3,
          border: "1px solid",
          borderColor: alpha(theme.palette.divider, 0.9),
          background: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.55 : 0.92),
          backdropFilter: "blur(12px)",
        }}
      >
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            useFlexGap
            spacing={1}
            sx={{ mb: 1, gap: 1 }}
          >
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
              <GroupsIcon sx={{ color: portal.pink }} />
              <Typography variant="h6" sx={{ fontWeight: 800, fontSize: "1.1rem", color: portal.pink }}>
                Remaining Downline
              </Typography>
              <Chip
                size="small"
                label={referrals.length}
                sx={{
                  fontWeight: 700,
                  color: "common.white",
                  border: "1px solid",
                  borderColor: alpha(portal.cyan, 0.45),
                  bgcolor: alpha(portal.cyan, 0.16),
                }}
              />
            </Stack>
            {referrals.length > 0 && (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                {anyDownlineAdGoalOpen && (
                  <Tooltip title="Today's ad goal not completed yet (UTC day) for one or more downline members.">
                    <WarningAmberIcon sx={{ fontSize: 20, color: "warning.main" }} aria-hidden />
                  </Tooltip>
                )}
                <Typography
                  variant="caption"
                  component="span"
                  sx={{
                    fontFamily: "ui-monospace, monospace",
                    fontSize: "0.7rem",
                    color: anyDownlineAdGoalOpen ? "warning.light" : "text.secondary",
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatUtcMidnightCountdown(utcMidnightRemainingMs)}
                </Typography>
              </Stack>
            )}
          </Stack>

          {referrals.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No referrals found.
            </Typography>
          ) : (
            <Stack spacing={1.25}>
              {referrals.map((ref) => (
                  <Box
                    key={ref.userId}
                    sx={{
                      border: "1px solid",
                      borderColor: alpha(theme.palette.divider, 0.85),
                      borderRadius: 2.5,
                      p: 1.25,
                      background:
                        theme.palette.mode === "dark"
                          ? `linear-gradient(145deg, ${alpha("#1a1f2e", 0.9)} 0%, ${alpha("#10131d", 0.9)} 100%)`
                          : alpha(theme.palette.background.paper, 0.95),
                      boxShadow: `inset 0 1px 0 ${alpha("#fff", 0.06)}`,
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.75}
                      flexWrap={{ xs: "nowrap", sm: "wrap" }}
                      useFlexGap
                      sx={{ width: "100%", minWidth: 0, overflow: "hidden" }}
                    >
                      <Typography
                        component={RouterLink}
                        to="/dashboard/users-activity"
                        variant="body2"
                        title={`${ref.name} — Users Activity`}
                        sx={{
                          fontWeight: 800,
                          color: "common.white",
                          mr: 0.25,
                          flex: { xs: "1 1 0%", sm: "none" },
                          minWidth: { xs: 0, sm: `${referralLayoutChars.nameChars}ch` },
                          maxWidth: `${referralLayoutChars.nameChars}ch`,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "inline-block",
                          textDecoration: "none",
                          "&:hover": {
                            textDecoration: "underline",
                            textUnderlineOffset: "0.12em",
                          },
                        }}
                      >
                        {truncateToMaxChars(ref.name, referralNameMaxChars)}
                      </Typography>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.75}
                        useFlexGap
                        sx={{
                          flexShrink: 0,
                          marginLeft: "auto",
                          minWidth: 0,
                        }}
                      >
                        <Box
                          sx={{
                            border: "1px solid",
                            borderColor: alpha(theme.palette.divider, 0.85),
                            borderRadius: 2,
                            px: 1,
                            py: 0.4,
                            flexShrink: 0,
                            background:
                              theme.palette.mode === "dark"
                                ? `linear-gradient(145deg, ${alpha("#1a1f2e", 0.9)} 0%, ${alpha("#10131d", 0.9)} 100%)`
                                : alpha(theme.palette.background.paper, 0.95),
                          }}
                        >
                          <Box
                            sx={{
                              minWidth: `${referralLayoutChars.rankChars}ch`,
                              maxWidth: `${referralLayoutChars.rankChars}ch`,
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 800,
                                color: theme.palette.success.main,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textAlign: "center",
                                width: "100%",
                              }}
                            >
                              {ref.rank}
                            </Typography>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            border: "1px solid",
                            borderColor: alpha(theme.palette.divider, 0.85),
                            borderRadius: 2,
                            px: 1,
                            py: 0.4,
                            flexShrink: 0,
                            background:
                              theme.palette.mode === "dark"
                                ? `linear-gradient(145deg, ${alpha("#1a1f2e", 0.9)} 0%, ${alpha("#10131d", 0.9)} 100%)`
                                : alpha(theme.palette.background.paper, 0.95),
                          }}
                        >
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="caption" color="text.secondary">
                              EP :
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 800,
                                color: "common.white",
                                minWidth: `${referralLayoutChars.epChars}ch`,
                                maxWidth: `${referralLayoutChars.epChars}ch`,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                display: "inline-block",
                                textAlign: "left",
                              }}
                            >
                              {ref.engagementPoints.toLocaleString()}
                            </Typography>
                          </Stack>
                        </Box>
                        <Box
                          sx={{
                            border: "1px solid",
                            borderColor: isStreakGoalMetToday(ref.adsWatchedToday, ref.goalPerDay)
                              ? alpha(theme.palette.divider, 0.85)
                              : alpha(theme.palette.warning.main, 0.55),
                            borderRadius: 2,
                            px: 1,
                            py: 0.4,
                            flexShrink: 0,
                            background:
                              theme.palette.mode === "dark"
                                ? `linear-gradient(145deg, ${alpha("#1a1f2e", 0.9)} 0%, ${alpha("#10131d", 0.9)} 100%)`
                                : alpha(theme.palette.background.paper, 0.95),
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 800, color: "common.white" }}>
                            {ref.currentStreak} 🔥
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>
                    <Box sx={{ mt: 1 }}>
                      <ThinLadderBar
                        theme={theme}
                        emoji="🎯"
                        fillPercent={ref.todayGoalPercent ?? 0}
                        label={`Referral today's goal progress (${ref.adsWatchedToday ?? 0}/${ref.goalPerDay ?? 10})`}
                      />
                    </Box>
                  </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={copySnack.open}
        autoHideDuration={2600}
        onClose={(_, reason) => {
          if (reason === "clickaway") return;
          setCopySnack((s) => ({ ...s, open: false }));
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setCopySnack((s) => ({ ...s, open: false }))}
          severity={copySnack.severity}
          variant="filled"
          elevation={6}
          sx={{
            alignItems: "center",
            fontWeight: 600,
            fontSize: "0.875rem",
            py: 0.5,
            px: 1,
            minWidth: "unset",
            maxWidth: { xs: "92vw", sm: 360 },
          }}
        >
          {copySnack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
